import { useCallback, useState } from "react"

import type { CommittedMove, Move } from "../sortaballs-8-colors/logic"

import { useHelperReplay } from "../shared-sortaballs/use-helper-replay"

interface UseIncrementalReplayInput {
	clearSelection: ()=> void
	commitSelectPlate: (index: number)=> void
	committedMoves: CommittedMove[]
	demoSelectPlate: (index: number)=> void
	moves: Move[]
	reset: ()=> void
}

export const INITIAL_REVEAL_COUNT = 5

// Number of leading committed moves that replay the solver's opening prefix
// exactly. correctMoves are single-ball moves, so any other committed move (a
// player move, a group move, an undo) breaks the baseline at that index.
const matchingPrefixLength = (
	committed: CommittedMove[],
	correct: Move[],
): number=> {
	let i = 0
	while (i < committed.length && i < correct.length) {
		const expected = correct[i]
		const actual = committed[i]
		if (
			!expected ||
			!actual ||
			actual.count !== 1 ||
			actual.from !== expected.from ||
			actual.to !== expected.to
		) {
			break
		}
		i++
	}
	return i
}

// Sortaballs' incremental help reveal. Help plays REAL committed moves, so the
// revealed prefix is derived from the engine's own move list: a restored
// session continues where it stopped, undoing a revealed move makes the next
// press re-reveal exactly that move, and a foreign player move (broken
// baseline) restarts the reveal from the initial state.
export function useIncrementalReplay(input: UseIncrementalReplayInput) {
	const {
		clearSelection,
		commitSelectPlate,
		committedMoves,
		demoSelectPlate,
		moves,
		reset,
	} = input
	const { isPlaying, revealNextMove, startReplay } = useHelperReplay({
		clearSelection,
		commitSelectPlate,
		demoSelectPlate,
		moves,
		reset,
	})
	// While a replay animates, `committedMoves` grows move by move — the badge
	// must keep showing the requested target instead of the partial prefix.
	const [targetCount, setTargetCount] = useState(0)
	const prefixLength = matchingPrefixLength(committedMoves, moves)
	const baselineIntact = prefixLength === committedMoves.length
	const baseCount = isPlaying ? targetCount : prefixLength
	const nextCount = Math.min(
		baseCount === 0 ? INITIAL_REVEAL_COUNT : baseCount + 1,
		moves.length,
	)
	const canReveal = moves.length > 0 && !isPlaying && prefixLength < moves.length

	const revealNext = useCallback(()=> {
		if (!canReveal) return
		const target = nextCount
		setTargetCount(target)
		// First press (no baseline yet) or a broken baseline → replay from the
		// initial state; otherwise play just the next move from the board.
		if (prefixLength > 0 && baselineIntact) {
			revealNextMove(moves[prefixLength], { commit: true })
		} else {
			startReplay({ commit: true, count: target })
		}
	}, [
		baselineIntact,
		canReveal,
		moves,
		nextCount,
		prefixLength,
		revealNextMove,
		startReplay,
	])

	return {
		canReveal,
		isPlaying,
		nextCount,
		revealedCount: baseCount,
		revealNext,
		startReplay,
	}
}
